import { useMemo, useState } from 'react';
import type { WidgetType } from '@noorview/core';
import { WIDGETS, WIDGET_DESIGNS, addDays, appealTotals, designOf, money, ymd } from '@noorview/core';
import { useApp } from '../data/store';
import { Segmented, Sheet } from '../ui/controls';

/**
 * Analytics: how the masjid's screens are used, worked out from its own records only: time each display was on
 * (from its heartbeats), what was sent live and by whom (the activity log), what's on the screens (their designs),
 * and messages and appeals. Every figure says what it's based on, so nothing is guessed.
 */

const hours = (s: number) => (s >= 3600 ? `${(s / 3600).toFixed(s >= 36_000 ? 0 : 1)} h` : `${Math.round(s / 60)} min`);
const DAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

function Bars({ rows, format }: { rows: Array<{ label: string; value: number; sub?: string }>; format: (n: number) => string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="hint">Nothing yet.</p>;
  return (
    <div className="an-bars">
      {rows.map((r) => (
        <div key={r.label} className="an-bar">
          <span className="an-l">{r.label}{r.sub && <small>{r.sub}</small>}</span>
          <span className="an-track"><i style={{ width: `${(r.value / max) * 100}%` }} /></span>
          <b className="mono">{format(r.value)}</b>
        </div>
      ))}
    </div>
  );
}

export function AnalyticsSheet({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const [period, setPeriod] = useState<'7' | '30'>('7');
  const tz = ws.masjid.timezone;
  const a = useMemo(() => {
    const days = Number(period);
    const today = ymd(new Date(), tz);
    const from = addDays(today, -(days - 1));
    const since = Date.now() - days * 86_400_000;
    const names = new Map(ws.screens.map((s) => [s.id, s.name]));

    // Time on screen, from the displays' heartbeats.
    const up = ws.uptime.filter((u) => u.day >= from && names.has(u.screenId));
    const perScreen = ws.screens.map((s) => ({ label: s.name, value: up.filter((u) => u.screenId === s.id).reduce((n, u) => n + u.seconds, 0) })).sort((x, y) => y.value - x.value);
    const totalUp = perScreen.reduce((n, r) => n + r.value, 0);

    // What was changed, from the activity log.
    const acts = ws.activity.filter((x) => Date.parse(x.at) >= since);
    const published = acts.filter((x) => x.kind === 'published');
    const byScreen = new Map<string, number>();
    // Each "sent live" entry names its screen at the start of its title.
    const screenNames = [...names.values()].sort((p, q) => q.length - p.length);
    for (const x of published) { const n = screenNames.find((nm) => x.title.startsWith(nm)) ?? x.title.replace(/ (updated|is ready)$/, ''); byScreen.set(n, (byScreen.get(n) ?? 0) + 1); }
    const changes = [...byScreen].map(([label, value]) => ({ label, value })).sort((x, y) => y.value - x.value);
    const people = new Map<string, number>();
    for (const x of acts) { const who = x.detail.split(' · ')[0]; if (who && ['published', 'schedule', 'timetable', 'settings', 'salah', 'message', 'appeal', 'eid'].includes(x.kind)) people.set(who, (people.get(who) ?? 0) + 1); }
    const team = [...people].map(([label, value]) => ({ label, value })).sort((x, y) => y.value - x.value).slice(0, 6);
    const weekday = new Array(7).fill(0) as number[];
    for (const x of published) weekday[new Date(x.at).getDay()]! += 1;
    const busiest = weekday.some((n) => n) ? weekday.indexOf(Math.max(...weekday)) : -1;

    // What's on the screens now (their live designs), and in saved templates.
    const live = ws.screens.map((s) => ws.designs.find((d) => d.id === s.designId)).flatMap((d) => (d ? [d.published ?? d.draft] : []));
    const typeCount = new Map<WidgetType, number>();
    const designCount = new Map<string, number>();
    const styleCount = new Map<string, number>();
    for (const d of live) for (const w of d.widgets) {
      typeCount.set(w.type, (typeCount.get(w.type) ?? 0) + 1);
      styleCount.set(w.style, (styleCount.get(w.style) ?? 0) + 1);
      if (WIDGET_DESIGNS[w.type]) { const k = `${WIDGETS[w.type].label} · ${WIDGET_DESIGNS[w.type]!.find((x) => x.value === designOf(w))?.label ?? ''}`; designCount.set(k, (designCount.get(k) ?? 0) + 1); }
    }
    const widgets = [...typeCount].map(([t, value]) => ({ label: WIDGETS[t].label, value })).sort((x, y) => y.value - x.value);
    const designs = [...designCount].map(([label, value]) => ({ label, value })).sort((x, y) => y.value - x.value).slice(0, 6);
    const styles = [...styleCount].map(([s, value]) => ({ label: s === 'none' ? 'No panel' : s === '3d' ? '3D Drop' : s[0]!.toUpperCase() + s.slice(1), value })).sort((x, y) => y.value - x.value);

    // Studio work per screen (all time): saves and versions sent live.
    const studio = ws.screens.map((s) => { const d = ws.designs.find((x) => x.id === s.designId); return { label: s.name, value: d?.draftRevision ?? 0, sub: `${d?.publishedVersion ?? 0} sent live` }; }).sort((x, y) => y.value - x.value);

    const bcs = ws.broadcasts.filter((b) => Date.parse(b.startsAt) >= since);
    const messages = bcs.filter((b) => b.kind === 'message').length;
    const raised = bcs.filter((b) => b.appeal).reduce((n, b) => n + appealTotals(b.appeal!).raised, 0);
    const gifts = bcs.filter((b) => b.appeal).reduce((n, b) => n + b.appeal!.gifts.length, 0);

    const covered = ws.timetable.filter((d) => d.date >= today).length;

    // Plain-language notes drawn from the figures above.
    const notes: string[] = [];
    if (perScreen[0] && perScreen[0].value > 0) notes.push(`${perScreen[0].label} was on the longest: ${hours(perScreen[0].value)} over the last ${days} days, about ${hours(perScreen[0].value / days)} a day.`);
    const idle = perScreen.filter((r) => r.value === 0).map((r) => r.label);
    if (idle.length && idle.length < ws.screens.length) notes.push(`${idle.join(', ')} ${idle.length === 1 ? 'wasn’t' : 'weren’t'} connected at all in this period. Check the display is on and paired.`);
    if (changes[0]) notes.push(`${changes[0].label} is changed most often: sent live ${changes[0].value} time${changes[0].value === 1 ? '' : 's'}.`);
    if (busiest >= 0) notes.push(`Changes usually go live on ${DAYS[busiest]}.`);
    if (widgets[0]) notes.push(`Your most used widget is ${widgets[0].label}, on ${live.filter((d) => d.widgets.some((w) => WIDGETS[w.type].label === widgets[0]!.label)).length} of ${live.length} screens.`);
    if (covered < 30) notes.push(covered ? `Your timetable runs out in ${covered} day${covered === 1 ? '' : 's'}. Upload the next part soon.` : 'There’s no timetable for the days ahead, so screens show no times. Upload your CSV.');
    if (raised > 0) notes.push(`Live appeals raised ${money(raised)} from ${gifts} gift${gifts === 1 ? '' : 's'}.`);
    return { days, perScreen, totalUp, published: published.length, changes, team, widgets, designs, styles, studio, messages, raised, gifts, covered, notes };
  }, [ws, period, tz]);

  return (
    <Sheet open onClose={onClose} title="Analytics" subtitle="How your screens are used, from your own records." size="xl">
      <Segmented size="sm" label="Period" value={period} onChange={setPeriod} options={[{ value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' }]} className="an-period" />
      <div className="an-stats">
        <div className="an-stat ctl"><small>Time on screens</small><b>{hours(a.totalUp)}</b><span>All displays, connected</span></div>
        <div className="an-stat ctl"><small>Changes sent live</small><b>{a.published}</b><span>Across every screen</span></div>
        <div className="an-stat ctl"><small>Messages sent</small><b>{a.messages}</b><span>Urgent messages</span></div>
        <div className="an-stat ctl"><small>Raised in appeals</small><b>{money(a.raised)}</b><span>{a.gifts} gift{a.gifts === 1 ? '' : 's'}</span></div>
      </div>
      {a.notes.length > 0 && <section className="an-card ctl"><h3>What stands out</h3><ul className="an-notes">{a.notes.map((n) => <li key={n}>{n}</li>)}</ul></section>}
      <div className="an-grid">
        <section className="an-card ctl"><h3>Time on screen</h3><Bars rows={a.perScreen} format={hours} /><p className="hint">Counted from each display’s connection, every few seconds while it’s on.</p></section>
        <section className="an-card ctl"><h3>Most changed screens</h3><Bars rows={a.changes} format={(n) => `${n}×`} /><p className="hint">Times each screen was sent live in this period.</p></section>
        <section className="an-card ctl"><h3>Favourite widgets</h3><Bars rows={a.widgets} format={(n) => String(n)} /><p className="hint">Widgets on your screens right now.</p></section>
        <section className="an-card ctl"><h3>Favourite designs</h3><Bars rows={a.designs} format={(n) => String(n)} /></section>
        <section className="an-card ctl"><h3>Studio work, all time</h3><Bars rows={a.studio} format={(n) => `${n} saves`} /></section>
        <section className="an-card ctl"><h3>Who makes the changes</h3><Bars rows={a.team} format={(n) => String(n)} /><p className="hint">Changes sent live, scheduled or saved in Settings.</p></section>
        <section className="an-card ctl"><h3>Widget styles</h3><Bars rows={a.styles} format={(n) => String(n)} /></section>
      </div>
    </Sheet>
  );
}
